import React, { useReducer } from "react";
import DeclarationContext from "./DeclarationContext";
import declarationReducer from "./DeclarationReducer";
import {
	SET_ITEM_ELEMENT_VALUE,
	SET_ELEMENT_VALUE,
	CREATE_ITEM,
	DELETE_ITEM,
	UPDATE_STATE,
	UPDATE_ITEM,
	SET_ELEMENT_VALUE_IN_SETCION,
	SET_ITEM_ELEMENT_VALUE_IN_SECTION,
	SET_UNSAVED_CHANGES,
	LIST_DECLARATIONS_LOADING,
	LIST_DECLARATIONS,
	UPDATE_AMENDMENT_ITEM,
	DELETE_AMENDMENT_ITEM,
	CREATE_AMENDMENT_ITEM,
	SET_AMENDMENT_ITEM_ELEMENT_VALUE,
	SET_AMENDMENT_ELEMENT_VALUE,
	SET_NOTIFICATIONS,
	CLEAR_SELECTED_DECLARATION_STATE,
} from "../types";
import headerObligations from "../../constants/obligations/header-obligations";
import itemsObligations from "../../constants/obligations/items-obligations";
import { useHistory } from "react-router";
import {
	findElementObligationsUtil,
	getValidationErrorsUtil,
	parseNotificationObject,
	parseUiElementNotifications,
} from "./validations";
import { listAllDeclarations, submitDeclarationUpdate } from "../../api/declarations";
import { getQueryParam } from "../../router/utils";
import { cancelAmendmentUtil, createAmendmentUtil } from "./amendments";
import { AMENDMENT_QUERY_PARAM } from "../../constants/GlobalConstants";

export const initialState = {
	declaration: null,
	declarationMetadata: {},
	notifications: [],
	mandatoryElements: [],
	disabledElements: [],
	validationErrors: [],
	mandatoryPulldownOptions: {},
	hasUnsavedChanges: false,
	declarations: [],
	declarationsLoading: false,
};

/** state allows us to access anything in the state and dispatch allows dispatching objects to the reducer
 * populate the declarationReducer with the initial state to instantiate it
 */
const DeclarationState = (props) => {
	const [state, dispatch] = useReducer(declarationReducer, initialState);
	const history = useHistory();

	const setDeclarations = (declarations) => {
		dispatch({
			type: LIST_DECLARATIONS,
			payload: declarations,
		});
	};

	const listDeclarations = () => {
		dispatch({
			type: LIST_DECLARATIONS_LOADING,
			payload: true,
		});

		// List all declarations per logged in user
		listAllDeclarations()
			.then((result) => {
				setDeclarations(result.items);
				setHasUnsavedChanges(false);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const setDeclaration = ({ declaration, metadata, notifications }) => {
		dispatch({
			type: UPDATE_STATE,
			payload: { declaration, metadata, notifications },
		});
	};

	const clearSelectedDeclaration = () => {
		dispatch({
			type: CLEAR_SELECTED_DECLARATION_STATE
		})
	}

	const updateDeclaration = async (creationTime, metadata, declaration, isAmendment) => {
		const isSelectedDeclaration = creationTime === state.declarationMetadata.creationTime;
		let updatedDeclarations = [...state.declarations];
		let existingDeclaration = updatedDeclarations.find(
			(declaration) => declaration.creationTime === creationTime
		);

		if (!existingDeclaration) {
			return;
		}

		await submitDeclarationUpdate(
			{ ...existingDeclaration.jdeclaration.meta, ...metadata, creationTime },
			declaration || existingDeclaration.jdeclaration.data.declaration,
			isAmendment
		);

		if (isSelectedDeclaration) {
			existingDeclaration.jdeclaration.data.declaration = state.declaration;

			state.declarationMetadata = metadata;
		} else if (declaration) {
			existingDeclaration.jdeclaration.data.declaration = declaration;
		}

		existingDeclaration.jdeclaration.meta = metadata;
	};

	/** The notifications array returned from the back-end inside the declaration object is an array of objects.
	 * Each object consists of information about the notification itself (functionCode, and other data that would be displayed to the user)
	 * And a "uiElements" array, which consists of an array of errors related to the declaration elements that the user can go through and fix.
	 * The notification object itself will be displayed as an item in the notifications list, and each one of the entries in uiElements will be as well.
	 */
	const setNotifications = (notifications) => {
		let allNotifications = [];
		let uiElementNotifications = [];

		// existingNotifications
		// 	?.sort((a, b) => (a.processedTime > b.processedTime ? -1 : 1))
		// 	.forEach((notification) => {
		// 		const parsedNotification = parseNotificationObject(notification);

		// 		if (parsedNotification) {
		// 			allNotifications.push(parsedNotification);
		// 		}

		// 		uiElementNotifications = [
		// 			...uiElementNotifications,
		// 			...notification.uIElements.map((element) => ({
		// 				...element,
		// 				processedTime: notification.processedTime,
		// 			})),
		// 		];
		// 	});

		notifications
			?.sort((a, b) => (a.processedTime > b.processedTime ? -1 : 1))
			.forEach((notification) => {
				const parsedNotification = parseNotificationObject(notification);

				if (parsedNotification) {
					allNotifications.push(parsedNotification);
				}

				uiElementNotifications = [
					...uiElementNotifications,
					...notification.uIElements.map((element) => ({
						...element,
						processedTime: notification.processedTime,
					})),
				];
			});

		allNotifications = [...allNotifications, ...parseUiElementNotifications(uiElementNotifications)];
		dispatch({
			type: SET_NOTIFICATIONS,
			payload: allNotifications,
		});
	};

	const setElementValue = (name, value) => {
		const isAmendment = getQueryParam(AMENDMENT_QUERY_PARAM);

		if (isAmendment === "true") {
			dispatch({
				type: SET_AMENDMENT_ELEMENT_VALUE,
				payload: {
					name,
					value,
				},
			});
		} else {
			dispatch({
				type: SET_ELEMENT_VALUE,
				payload: {
					name,
					value,
				},
			});
		}
	};

	const setElementValueInSection = (section, name, value) => {
		dispatch({
			type: SET_ELEMENT_VALUE_IN_SETCION,
			payload: {
				name,
				value,
				section,
			},
		});
	};

	const setItemElementValue = (itemIndex, name, value) => {
		const isAmendment = getQueryParam(AMENDMENT_QUERY_PARAM);
		if (isAmendment === "true") {
			dispatch({
				type: SET_AMENDMENT_ITEM_ELEMENT_VALUE,
				payload: {
					itemIndex,
					name,
					value,
				},
			});
		} else {
			dispatch({
				type: SET_ITEM_ELEMENT_VALUE,
				payload: {
					itemIndex,
					name,
					value,
				},
			});
		}
	};

	const setItemElementValueInSection = (itemIndex, section, name, value) => {
		dispatch({
			type: SET_ITEM_ELEMENT_VALUE_IN_SECTION,
			payload: {
				itemIndex,
				name,
				value,
				section,
			},
		});
	};

	const setHasUnsavedChanges = (hasUnsavedChanges) => {
		dispatch({
			type: SET_UNSAVED_CHANGES,
			payload: hasUnsavedChanges,
		});
	};

	const createItem = (name) => {
		const isAmendment = getQueryParam(AMENDMENT_QUERY_PARAM);

		if (isAmendment === "true") {
			dispatch({
				type: CREATE_AMENDMENT_ITEM,
				payload: {
					name,
					containerIdentificationNumber: "number(s) unknown",
					insertTimeStamp: new Date().toISOString(),
				},
			});
		} else {
			dispatch({
				type: CREATE_ITEM,
				payload: {
					name,
					containerIdentificationNumber: "number(s) unknown",
					insertTimeStamp: new Date().toISOString(),
				},
			});
		}
	};

	const updateItem = (itemIndex, updates) => {
		const isAmendment = getQueryParam(AMENDMENT_QUERY_PARAM);

		if (isAmendment === "true") {
			dispatch({
				type: UPDATE_AMENDMENT_ITEM,
				payload: {
					itemIndex,
					updates: {
						...updates,
						updateTimeStamp: new Date().toISOString(),
					},
				},
			});
		} else {
			dispatch({
				type: UPDATE_ITEM,
				payload: {
					itemIndex,
					updates: {
						...updates,
						updateTimeStamp: new Date().toISOString(),
					},
				},
			});
		}
	};

	const deleteItem = (itemIndex) => {
		const isAmendment = getQueryParam(AMENDMENT_QUERY_PARAM);

		if (isAmendment === "true") {
			dispatch({
				type: DELETE_AMENDMENT_ITEM,
				payload: { itemIndex },
			});
		} else {
			dispatch({
				type: DELETE_ITEM,
				payload: { itemIndex },
			});
		}
	};

	/** Recursively loop through nested objects in the DeclarationState to find the value of a provided key */
	const findValueInContext = (selectedKey, level) => {
		let value = null;

		const findValue = (object) => {
			if (object) {
				Object.keys(object).forEach((key) => {
					if (typeof object[key] === "object") {
						if (key === selectedKey) {
							value = object[key];
						} else if (!Array.isArray(object[key])) {
							findValue(object[key]);
						}
					} else {
						if (key === selectedKey) {
							value = object[key];
						}
					}
				});
			}
		};

		if (state.declaration) {
			const { declarationItems, ...headerElements } = state.declaration;

			if (level === "item") {
				findValue(declarationItems);
			} else if (level === "header") {
				findValue(headerElements);
			} else {
				findValue(state.declaration);
			}
		}
		return value;
	};

	/** Check if an element has been flagged as readOnly
	 * Currently elements will be set to be readOnly if they're specified in one of the obligiation files, in the "services" section
	 * with a value and not an empty string, if it has an empty string, then it will be set to be mandatory only by
	 * the findElementObligations action
	 */

	const findInServiceObligation = (obligations, elementName) => {
		let result = null;
		Object.keys(obligations).forEach((serviceKey) => {
			if (state.declaration.service === serviceKey) {
				Object.keys(obligations[serviceKey]).forEach((elementKey) => {
					if (obligations[serviceKey][elementKey] && elementKey === elementName) {
						result = obligations[serviceKey][elementKey];
					}
				});
			}
		});

		return result;
	};

	const isElementReadOnly = (elementName) => {
		let readOnly = false;

		if (state.declaration?.service) {
			// TODO: Update to work with the new tab query params
			if (history.location.pathname.startsWith("/items")) {
				readOnly = Boolean(findInServiceObligation(itemsObligations.services, elementName));
			} else {
				readOnly = Boolean(findInServiceObligation(headerObligations.services, elementName));
			}
		}

		return readOnly;
	};

	/** Returns read only array items of multiline table elements */
	const getElementObligation = (elementName) => {
		let element = null;

		if (state.declaration.service) {
			if (history.location.pathname.startsWith("/items")) {
				element = findInServiceObligation(itemsObligations.services, elementName);
			} else {
				element = findInServiceObligation(headerObligations.services, elementName);
			}
		}

		return element;
	};

	const getValidationErrors = () => {
		return getValidationErrorsUtil(state, dispatch, findValueInContext);
	};

	const findElementObligations = () => {
		return findElementObligationsUtil(state, dispatch, findValueInContext);
	};

	const createAmendment = () => {
		return createAmendmentUtil(state, dispatch);
	};

	const cancelAmendment = () => {
		return cancelAmendmentUtil(state, dispatch);
	};

	const printState = async () => {
		const json = JSON.stringify(state.declaration);
		const blob = new Blob([json], { type: "application/json" });
		const href = await URL.createObjectURL(blob);
		const link = document.createElement("a");

		link.href = href;
		link.download = "declaration.json";

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	return (
		<DeclarationContext.Provider
			value={{
				state: state || {},
				updateDeclaration,
				setNotifications,
				setDeclarations,
				setHasUnsavedChanges,
				listDeclarations,
				setDeclaration,
				setElementValueInSection,
				setItemElementValueInSection,
				createItem,
				deleteItem,
				updateItem,
				setElementValue,
				setItemElementValue,
				findElementObligations,
				findValueInContext,
				getElementObligation,
				isElementReadOnly,
				printState,
				getValidationErrors,
				createAmendment,
				cancelAmendment,
				clearSelectedDeclaration
			}}
		>
			{props.children}
		</DeclarationContext.Provider>
	);
};

export default DeclarationState;
